/**
 * Main Layout Component
 *
 * Provides consistent page structure with Navigation and Footer.
 * All main-site pages (non-Cosmos) should be wrapped in this layout.
 *
 * Dark theme layout using the AURA-inspired design tokens from Layer 1A.
 */

import { useState, createContext, useContext, useCallback, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, Outlet } from 'react-router-dom';
import { Navigation } from '@/components/shared/Navigation';
import { Footer } from '@/components/shared/Footer';
import { ToastContainer, type ToastProps, type ToastType } from '@/components/shared/Toast';

export interface LayoutProps {
  /** Page content */
  children: ReactNode;
  /** Whether to show navigation */
  showHeader?: boolean;
  /** Whether to show footer */
  showFooter?: boolean;
  /** Layout variant - 'default' for standard pages, 'minimal' for bento single-viewport */
  variant?: 'default' | 'minimal';
}

// Toast context for showing toasts from anywhere in the app
interface ToastContextType {
  showToast: (message: string, options?: { title?: string; type?: ToastType; duration?: number }) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a Layout component');
  }
  return context;
}

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const pageTransition = {
  type: 'tween',
  ease: 'easeInOut',
  duration: 0.3,
};

/**
 * Layout Component
 *
 * @example
 * ```tsx
 * <Layout>
 *   <HomePage />
 * </Layout>
 * ```
 */
export function Layout({
  children,
  showHeader = true,
  showFooter = true,
  variant = 'default',
}: LayoutProps) {
  const location = useLocation();
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const showToast = useCallback(
    (
      message: string,
      options?: { title?: string; type?: ToastType; duration?: number }
    ) => {
      const newToast: Omit<ToastProps, 'onDismiss'> = {
        id: `toast-${Date.now()}`,
        message,
        type: options?.type ?? 'info',
        duration: options?.duration ?? 5000,
      };
      if (options?.title) {
        (newToast as ToastProps).title = options.title;
      }
      setToasts((prev) => [...prev, newToast as ToastProps]);
    },
    []
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const isMinimal = variant === 'minimal';

  return (
    <ToastContext.Provider value={{ showToast }}>
      <div className={`flex flex-col ${isMinimal ? 'h-screen overflow-hidden' : 'min-h-screen'} bg-bg-primary`}>
        {/* Navigation */}
        {showHeader && <Navigation />}

        {/* Main content with page transitions */}
        <AnimatePresence mode="wait">
          <motion.main
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
            className={`flex-1 ${isMinimal ? 'overflow-hidden' : ''}`}
          >
            {children}
          </motion.main>
        </AnimatePresence>

        {/* Footer */}
        {showFooter && !isMinimal && <Footer />}
        {showFooter && isMinimal && <MinimalFooter />}

        {/* Toast notifications */}
        <ToastContainer position="bottom-right" toasts={toasts} onDismiss={dismissToast} />
      </div>
    </ToastContext.Provider>
  );
}

/** Minimal footer for bento layout */
function MinimalFooter() {
  return (
    <footer className="bg-bg-surface border-t border-border-subtle py-2 px-4">
      <div className="container mx-auto flex items-center justify-between text-xs text-text-muted">
        <span>&copy; 2026 Kelley SD</span>
        <div className="flex gap-4">
          <a href="/privacy" className="hover:text-text-primary transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-text-primary transition-colors">Terms</a>
          <a href="mailto:brian@kelleysd.com" className="hover:text-text-primary transition-colors">Contact</a>
        </div>
      </div>
    </footer>
  );
}

/**
 * OutletLayout — Layout variant for React Router nested routes.
 * Use as a route element to wrap child routes with Nav + Footer.
 *
 * @example
 * ```tsx
 * <Route element={<OutletLayout />}>
 *   <Route path="/" element={<HomePage />} />
 * </Route>
 * ```
 */
export function OutletLayout(props: Omit<LayoutProps, 'children'>) {
  return (
    <Layout {...props}>
      <Outlet />
    </Layout>
  );
}

export default Layout;
